import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import { asId, type PersonId } from '@shared/brand';
import type { ISalaryRepository, RepositoryError, SetSalaryInput } from '@application/ports';
import type { SalaryRecord } from '@domain/entities/Compensation';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { firebase } from '../firebase/client';
import { translateCallableError } from './support';

/**
 * El sueldo, contra Firestore — y **las dos operaciones por callable**.
 *
 * Es el único repositorio del portal que no lee directo, y la razón es el permiso
 * fino: una regla de Firestore **no puede leer la matriz**, sólo comparar el rol.
 * Mientras el sueldo se leyera directo, `ver_salario` no gobernaba nada —
 * concedido a una líder no le abría el documento, quitado a RR.HH. no se lo
 * cerraba— y con los permisos por persona eso pasaba de ser una imprecisión a ser
 * una promesa falsa en pantalla.
 *
 * Pasando por `getSalary`, el permiso decide de verdad, incluidas las excepciones
 * de cada quien. Y las reglas se quedan negando toda lectura de cliente, que es
 * el piso más simple de razonar: aquí no entra nadie sin pasar por el servidor.
 *
 * **Sin sueldo registrado se devuelve `null`, no un error**: es el estado de casi
 * todo el padrón, y la ficha lo enseña como «Sin registrar».
 */
export class FirestoreSalaryRepository implements ISalaryRepository {
  async getFor(personId: PersonId): Promise<Result<SalaryRecord | null, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<{ personId: string }, { salary: RawSalary | null }>(
        functions,
        'getSalary',
      );
      const { data } = await invoke({ personId });
      if (data.salary === null) return ok(null);

      const effectiveFrom = CalendarDate.fromISO(data.salary.effectiveFrom);
      return ok({
        personId: asId<'PersonId'>(data.salary.personId),
        monthlyCents: data.salary.monthlyCents,
        /* Una fecha ilegible no tumba la ficha: el importe sigue siendo legible y
           es lo que se vino a ver. */
        effectiveFrom: effectiveFrom.ok ? effectiveFrom.value : CalendarDate.fromISOOrThrow('1970-01-01'),
        setByPersonId: asId<'PersonId'>(data.salary.setByPersonId),
        updatedAt:
          data.salary.updatedAt === undefined ? new Date(0) : new Date(data.salary.updatedAt),
      });
    } catch (cause) {
      return err(translateCallableError(cause, 'getSalary'));
    }
  }

  async set(input: SetSalaryInput): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<SetSalaryInput, void>(functions, 'setSalary');
      await invoke(input);
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'setSalary'));
    }
  }
}

interface RawSalary {
  readonly personId: string;
  readonly monthlyCents: number;
  readonly effectiveFrom: string;
  readonly setByPersonId: string;
  readonly updatedAt?: string;
}
